import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const Rules = () => {
  const [rules, setRules] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    rule_type: "max_hours",
    name: "",
    value: {},
    enabled: true,
    is_hard: true
  });

  const ruleTypes = [
    { 
      value: "max_hours", 
      label: "Maximum Hours Per Week",
      description: "Limit the maximum hours an employee can work per week",
      default_hard: true,
      fields: [{ key: "hours", label: "Hours", type: "number", default: 40 }]
    },
    { 
      value: "min_hours", 
      label: "Minimum Hours Per Week",
      description: "Require each employee to work at least this many hours per week",
      default_hard: true,
      fields: [{ key: "hours", label: "Hours", type: "number", default: 16 }]
    },
    { 
      value: "min_rest", 
      label: "Minimum Rest Between Shifts",
      description: "Ensure employees have adequate rest between shifts",
      default_hard: true,
      fields: [{ key: "hours", label: "Hours", type: "number", default: 8 }]
    },
    { 
      value: "max_consecutive_days", 
      label: "Maximum Consecutive Workdays",
      description: "No employee works more than this many days in a row",
      default_hard: true,
      fields: [{ key: "days", label: "Days", type: "number", default: 6 }]
    },
    { 
      value: "min_consecutive_days_off", 
      label: "Minimum Consecutive Days Off",
      description: "After a work block, employees must have at least this many days off",
      default_hard: true,
      fields: [{ key: "days", label: "Days", type: "number", default: 2 }]
    },
    { 
      value: "min_shifts_per_period", 
      label: "Minimum Shifts Per Roster",
      description: "Each employee must work at least this many shifts in the roster period",
      default_hard: true,
      fields: [{ key: "count", label: "Min Shifts", type: "number", default: 5 }]
    },
    { 
      value: "max_shifts_per_period", 
      label: "Maximum Shifts Per Roster",
      description: "Each employee can work at most this many shifts in the roster period",
      default_hard: true,
      fields: [{ key: "count", label: "Max Shifts", type: "number", default: 30 }]
    },
    { 
      value: "min_staff", 
      label: "Minimum Staff Per Shift (Global)",
      description: "Ensure minimum coverage for each shift across all types",
      default_hard: true,
      fields: [{ key: "count", label: "Minimum Staff", type: "number", default: 1 }]
    },
    { 
      value: "min_staff_per_shift", 
      label: "Minimum Staff Per Shift Type",
      description: "Set minimum staff requirement for a specific shift type",
      default_hard: true,
      fields: [
        { key: "shift_id", label: "Shift Type", type: "shift_select", default: "" },
        { key: "count", label: "Minimum Staff", type: "number", default: 1 }
      ]
    },
    { 
      value: "weekend_distribution", 
      label: "Fair Weekend Distribution",
      description: "Distribute weekend shifts fairly among employees (preference)",
      default_hard: false,
      fields: [{ key: "enabled", label: "Enable Fair Distribution", type: "boolean", default: true }]
    },
    { 
      value: "skill_requirement", 
      label: "Skill Requirement",
      description: "Require specific skills for certain shifts",
      default_hard: false,
      fields: [
        { key: "skill", label: "Required Skill", type: "text", default: "" },
        { key: "shift_name", label: "Shift Name (optional)", type: "text", default: "" }
      ]
    },
    { 
      value: "custom", 
      label: "Custom Rule",
      description: "Define a custom scheduling rule with a free-form description (treated as a soft preference)",
      default_hard: false,
      fields: [
        { key: "description", label: "Rule Description", type: "textarea", default: "" },
        { key: "constraint", label: "Constraint (e.g., 'No more than 3 consecutive night shifts')", type: "text", default: "" }
      ]
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/rules`, { withCredentials: true }),
        axios.get(`${API}/shifts`, { withCredentials: true })
      ]);
      setRules(rulesRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load rules");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const ruleType = ruleTypes.find(r => r.value === formData.rule_type);
    const name = formData.name || ruleType?.label || formData.rule_type;
    
    try {
      if (editingRule) {
        await axios.put(`${API}/rules/${editingRule.rule_id}`, {
          ...formData,
          name
        }, { withCredentials: true });
        toast.success("Rule updated");
      } else {
        await axios.post(`${API}/rules`, {
          ...formData,
          name
        }, { withCredentials: true });
        toast.success("Rule created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save rule:", error);
      toast.error("Failed to save rule");
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      rule_type: rule.rule_type,
      name: rule.name,
      value: rule.value,
      enabled: rule.enabled,
      is_hard: rule.is_hard !== undefined ? rule.is_hard : !["weekend_distribution", "skill_requirement", "custom"].includes(rule.rule_type)
    });
    setDialogOpen(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;
    
    try {
      await axios.delete(`${API}/rules/${ruleId}`, { withCredentials: true });
      toast.success("Rule deleted");
      fetchData();
    } catch (error) {
      console.error("Failed to delete rule:", error);
      toast.error("Failed to delete rule");
    }
  };

  const handleToggle = async (rule) => {
    try {
      await axios.put(`${API}/rules/${rule.rule_id}`, {
        ...rule,
        enabled: !rule.enabled
      }, { withCredentials: true });
      toast.success(rule.enabled ? "Rule disabled" : "Rule enabled");
      fetchData();
    } catch (error) {
      console.error("Failed to toggle rule:", error);
      toast.error("Failed to toggle rule");
    }
  };

  const resetForm = () => {
    setEditingRule(null);
    const defaultType = ruleTypes[0];
    const defaultValue = {};
    defaultType.fields.forEach(f => { defaultValue[f.key] = f.default; });
    
    setFormData({
      rule_type: defaultType.value,
      name: "",
      value: defaultValue,
      enabled: true,
      is_hard: defaultType.default_hard ?? true
    });
  };

  const handleTypeChange = (type) => {
    const ruleType = ruleTypes.find(r => r.value === type);
    const defaultValue = {};
    ruleType.fields.forEach(f => { defaultValue[f.key] = f.default; });
    
    setFormData({
      ...formData,
      rule_type: type,
      name: ruleType.label,
      value: defaultValue,
      is_hard: ruleType.default_hard ?? true
    });
  };

  const updateValue = (key, val) => {
    setFormData({
      ...formData,
      value: { ...formData.value, [key]: val }
    });
  };

  const getRuleTypeInfo = (type) => {
    return ruleTypes.find(r => r.value === type);
  };

  const getShiftName = (shiftId) => {
    const shift = shifts.find(s => s.shift_id === shiftId);
    return shift?.name || shiftId;
  };

  const formatRuleValue = (rule) => {
    switch (rule.rule_type) {
      case "max_hours":
        return `${rule.value.hours || 40} hours/week max`;
      case "min_hours":
        return `${rule.value.hours || 16} hours/week min`;
      case "min_rest":
        return `${rule.value.hours || 8} hours rest`;
      case "max_consecutive_days":
        return `${rule.value.days || 6} days max in a row`;
      case "min_consecutive_days_off":
        return `${rule.value.days || 2} days off after work`;
      case "min_shifts_per_period":
        return `${rule.value.count || 5} shifts min per roster`;
      case "max_shifts_per_period":
        return `${rule.value.count || 30} shifts max per roster`;
      case "min_staff":
        return `${rule.value.count || 1} minimum`;
      case "min_staff_per_shift":
        return `${rule.value.count || 1} min on ${getShiftName(rule.value.shift_id)}`;
      case "weekend_distribution":
        return rule.value.enabled ? "Enabled" : "Disabled";
      case "skill_requirement":
        return `${rule.value.skill || "N/A"}${rule.value.shift_name ? ` (${rule.value.shift_name})` : ""}`;
      case "custom":
        return rule.value.description || rule.value.constraint || "Custom rule";
      default:
        return JSON.stringify(rule.value);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Scheduling Rules</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Configure rules that the AI will follow when generating rosters
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
            data-testid="add-rule-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Rule
          </Button>
        </div>

        {/* Rules List */}
        {rules.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No rules configured</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Add scheduling rules to customize roster generation</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
            <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {rules.map((rule) => {
                const ruleType = getRuleTypeInfo(rule.rule_type);
                return (
                  <div key={rule.rule_id} className="p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <button
                          onClick={() => handleToggle(rule)}
                          className={`mt-1 ${rule.enabled ? 'text-[#002FA7]' : 'text-zinc-300'}`}
                          data-testid={`toggle-rule-${rule.rule_id}`}
                        >
                          {rule.enabled ? (
                            <ToggleRight className="w-6 h-6" />
                          ) : (
                            <ToggleLeft className="w-6 h-6" />
                          )}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`text-sm font-medium ${rule.enabled ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}`}>
                              {rule.name}
                            </h3>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                              rule.is_hard
                                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            }`} data-testid={`rule-hardness-${rule.rule_id}`}>
                              {rule.is_hard ? "Hard" : "Soft"}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                              rule.rule_type === 'custom' 
                                ? 'bg-violet-50 text-violet-600' 
                                : rule.rule_type === 'min_staff_per_shift'
                                  ? 'bg-amber-50 text-amber-600'
                                  : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400'
                            }`}>
                              {ruleType?.label || rule.rule_type}
                            </span>
                          </div>
                          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                            {ruleType?.description}
                          </p>
                          <div className="mt-2">
                            <span className={`
                              text-sm tabular-nums font-medium
                              ${rule.enabled ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400 dark:text-zinc-500'}
                            `}>
                              {formatRuleValue(rule)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(rule)}
                          data-testid={`edit-rule-${rule.rule_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(rule.rule_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-rule-${rule.rule_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                {editingRule ? "Edit Rule" : "Add Rule"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
              <div>
                <Label htmlFor="rule_type" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Rule Type *
                </Label>
                <Select
                  value={formData.rule_type}
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger className="mt-1" data-testid="rule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ruleTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.rule_type === "custom" && (
                <div>
                  <Label htmlFor="custom-name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Rule Name *
                  </Label>
                  <Input
                    id="custom-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Max 3 consecutive night shifts"
                    className="mt-1"
                    data-testid="custom-rule-name"
                  />
                </div>
              )}

              {getRuleTypeInfo(formData.rule_type)?.fields.map((field) => (
                <div key={field.key}>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    {field.label}
                  </Label>
                  {field.type === "boolean" ? (
                    <div className="flex items-center gap-3 mt-2">
                      <Switch
                        checked={formData.value[field.key] ?? field.default}
                        onCheckedChange={(checked) => updateValue(field.key, checked)}
                        data-testid={`rule-field-${field.key}`}
                      />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">
                        {formData.value[field.key] ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  ) : field.type === "shift_select" ? (
                    <Select
                      value={formData.value[field.key] || ""}
                      onValueChange={(val) => updateValue(field.key, val)}
                    >
                      <SelectTrigger className="mt-1" data-testid={`rule-field-${field.key}`}>
                        <SelectValue placeholder="Select shift type" />
                      </SelectTrigger>
                      <SelectContent>
                        {shifts.map((shift) => (
                          <SelectItem key={shift.shift_id} value={shift.shift_id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: shift.color }} />
                              {shift.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : field.type === "textarea" ? (
                    <Textarea
                      value={formData.value[field.key] ?? field.default}
                      onChange={(e) => updateValue(field.key, e.target.value)}
                      placeholder="Describe the rule in detail..."
                      className="mt-1"
                      rows={3}
                      data-testid={`rule-field-${field.key}`}
                    />
                  ) : (
                    <Input
                      type={field.type}
                      value={formData.value[field.key] ?? field.default}
                      onChange={(e) => updateValue(
                        field.key, 
                        field.type === "number" ? parseInt(e.target.value) || 0 : e.target.value
                      )}
                      className="mt-1"
                      data-testid={`rule-field-${field.key}`}
                    />
                  )}
                </div>
              ))}

              <div className="flex items-center justify-between py-3 border-t border-zinc-100 dark:border-zinc-800">
                <div>
                  <Label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Hard Rule
                  </Label>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">Hard rules are enforced as constraints — generation will fail if they can't be met. Soft rules are best-effort preferences.</p>
                </div>
                <Switch
                  checked={formData.is_hard}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_hard: checked })}
                  data-testid="rule-is-hard-switch"
                />
              </div>

              <div className="flex items-center justify-between py-3 border-t border-zinc-100 dark:border-zinc-800">
                <div>
                  <Label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Rule Enabled
                  </Label>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">Active rules are used in roster generation</p>
                </div>
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                  data-testid="rule-enabled-switch"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="cancel-rule-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-rule-btn"
                >
                  {editingRule ? "Update" : "Create"} Rule
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Rules;
